import { zodResolver } from '@hookform/resolvers/zod';
import {
  InvoiceApplicationFormData,
  InvoiceApplicationFormDataInput,
  InvoiceApplicationFormDataSchema,
  SelectionStateEnum,
  TransactableSourceType,
} from '@monetize/types/app';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../api/axios';
import {
  useApplyPaymentsAndCreditsToInvoices,
  useGetAggregatedTransactionApplicationsWithInvoices,
} from '../../../api/transactableService';
import {
  MAlert,
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MGrid,
  MSkeleton,
  MStack,
  MText,
} from '../../../components/Monetize';
import { MCurrency } from '../../../components/Monetize/MCurrency';
import { RefundApplicationTable } from '../../../components/TransactionApplication/RefundApplicationTable';
import {
  calculateAmounts,
  calculateApplicationFromData,
  calculateApplications,
} from '../../../components/TransactionApplication/TransactionApplicationUtils';
import { logger } from '../../../services/logger';

interface RefundApplicationModalProps {
  accountId: string;
  sourceId: string;
  currency: string;
  sourceOriginalAmount: number;
  billGroupId?: string;
  onClose: () => void;
  onContinue: () => void;
}

export const RefundApplicationModal = ({
  accountId,
  sourceId,
  currency,
  sourceOriginalAmount,
  billGroupId,
  onClose,
  onContinue,
}: RefundApplicationModalProps) => {
  const {
    data: aggregationResponse,
    isLoading,
    isError,
  } = useGetAggregatedTransactionApplicationsWithInvoices(
    {
      accountId,
      sourceId,
      sourceType: TransactableSourceType.payment,
      currency,
      billGroupId,
    },
    {
      refetchOnWindowFocus: false,
      meta: { showErrorToast: true },
    },
  );

  useEffect(() => {
    if (aggregationResponse) {
      reset(
        calculateApplicationFromData({
          ...aggregationResponse,
          allInvoices: aggregationResponse.appliedInvoices,
        }),
      );
    } else if (isError) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregationResponse, isError]);

  const {
    mutateAsync: doApplyPaymentsAndCreditsToInvoices,
    isPending: isSaving,
  } = useApplyPaymentsAndCreditsToInvoices(
    TransactableSourceType.payment,
    sourceId,
    accountId,
    {
      onError: (error) => {
        handleApiErrorToast(error);
      },
    },
  );

  const formState = useForm<
    InvoiceApplicationFormDataInput,
    unknown,
    InvoiceApplicationFormData
  >({
    resolver: zodResolver(InvoiceApplicationFormDataSchema),
    defaultValues: aggregationResponse
      ? calculateApplicationFromData({
          ...aggregationResponse,
          allInvoices: aggregationResponse.appliedInvoices,
        })
      : {
          checkedState: SelectionStateEnum.NONE,
          rows: [],
        },
  });

  const { handleSubmit, watch, reset } = formState;

  const rows = watch('rows');
  const aggregateTransactable = watch('aggregateTransactable');

  const { appliedAmount, availableAmount, totalAmount } = calculateAmounts(
    sourceOriginalAmount,
    rows,
    aggregateTransactable,
  );

  const hasRowErrors = rows.some(
    ({ appliedAmount: rowApplied, amountDueWithoutApplied }) =>
      Number(rowApplied) > amountDueWithoutApplied,
  );

  const isInvalidAmount = availableAmount < 0;

  const hasRefundableAmount = availableAmount > 0;

  const handleContinue = async (data: InvoiceApplicationFormData) => {
    try {
      const applications = calculateApplications(
        data,
        aggregationResponse?.aggregatedApplicationsById || {},
      );

      if (applications.length > 0) {
        await doApplyPaymentsAndCreditsToInvoices({ applications });
      }

      onClose();
      onContinue();
    } catch (ex) {
      logger.error(ex);
    }
  };

  function onError(error: unknown) {
    logger.error('RefundApplicationModal form error', error);
  }

  const hasInvoices =
    !!aggregationResponse && aggregationResponse.appliedInvoices.length > 0;

  return (
    <MCenterModal
      size="xl"
      isOpen
      onClose={onClose}
      modalTitle="Refund Payment"
      modalFooterProps={{ pt: 0, border: 0 }}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      renderFooter={() =>
        !isLoading ? (
          <MBox w="full">
            {isInvalidAmount && (
              <MAlert
                type="error"
                mb={6}
                alertProps={{ py: 2 }}
                alertIconProps={{ boxSize: 4 }}
                alertDescriptionProps={{ fontSize: 'sm' }}
              >
                Amount Applied cannot exceed Original Payment Amount.
              </MAlert>
            )}
            <MBox
              borderWidth="1px"
              borderStyle="solid"
              borderColor="border.subtle"
              borderRadius="lg"
              p="4"
              mx="4"
              mb="4"
              mt="2"
            >
              <MFlex justifyContent="space-between" mb={2}>
                <MText fontSize="sm">Original Payment Amount</MText>
                <MCurrency
                  amount={totalAmount}
                  options={{ currency, maximumFractionDigits: 2 }}
                  fontSize="sm"
                  textAlign="right"
                />
              </MFlex>
              <MFlex justifyContent="space-between" mb={2}>
                <MText fontSize="sm">Applied from this Payment</MText>
                <MCurrency
                  amount={-appliedAmount}
                  options={{ currency, maximumFractionDigits: 2 }}
                  fontSize="sm"
                  textAlign="right"
                />
              </MFlex>
              <MFlex justifyContent="space-between">
                <MText fontSize="lg" fontWeight="600">
                  Refundable Amount
                </MText>
                <MCurrency
                  amount={availableAmount}
                  options={{ currency, maximumFractionDigits: 2 }}
                  fontSize="lg"
                  fontWeight="600"
                  textAlign="right"
                />
              </MFlex>
            </MBox>
            <MStack
              gap={4}
              direction="row"
              align="center"
              justify="right"
              flex={1}
            >
              <MButton
                variant="cancel"
                minW="auto"
                isDisabled={isSaving}
                onClick={onClose}
              >
                Cancel
              </MButton>
              <MButton
                form="refund-application-form"
                type="submit"
                variant="primary"
                minW="auto"
                isDisabled={
                  rows.length === 0 ||
                  hasRowErrors ||
                  !hasRefundableAmount ||
                  isSaving
                }
                isLoading={isSaving}
              >
                Continue
              </MButton>
            </MStack>
          </MBox>
        ) : null
      }
    >
      <form
        id="refund-application-form"
        onSubmit={handleSubmit(handleContinue, onError)}
      >
        {isLoading ? (
          <RefundApplicationLoadingSkeleton />
        ) : (
          <>
            <MAlert
              type="info"
              mx={4}
              mb={4}
              alertDescriptionProps={{ fontSize: 'sm' }}
            >
              This payment is applied to the invoices below. Unapply the amount
              you'd like to refund before continuing.
            </MAlert>
            {!hasInvoices && (
              <MBox
                display="flex"
                flexDir="column"
                margin="0 auto"
                alignItems="center"
                justifyContent="center"
              >
                <MText fontWeight="bold" fontSize="lg">
                  There are no invoices applied to this payment.
                </MText>
              </MBox>
            )}
            {hasInvoices && (
              <RefundApplicationTable
                sourceType={TransactableSourceType.payment}
                totalAvailableAmount={availableAmount}
                formState={formState}
                availableAmount={availableAmount}
                isDisabled={isSaving}
              />
            )}
          </>
        )}
      </form>
    </MCenterModal>
  );
};

const RefundApplicationLoadingSkeleton = () => (
  <MBox padding="4" w="100%">
    <MGrid
      gridTemplateColumns="minmax(8rem, auto) repeat(2, minmax(auto, 1fr)) repeat(3, minmax(auto, 8rem))"
      columnGap={4}
      mb={2}
    >
      <MSkeleton height="39px" />
      <MSkeleton height="39px" />
      <MSkeleton height="39px" />
      <MSkeleton height="39px" />
      <MSkeleton height="39px" />
      <MSkeleton height="39px" />
    </MGrid>
    <MSkeleton height="39px" />
    <MSkeleton height="39px" mt="1" />
    <MSkeleton height="39px" mt="1" />
  </MBox>
);
